/**
 * Reine Funktionen fuer die Subscription-Billing-Historie (Spec 0222 §6, K1/K2).
 *
 * Kein React, keine Seiteneffekte — die Detail-Card/Timeline (Phase 5) konsumiert
 * ausschliesslich diese Helfer. Die K1/K2-Regeln stammen aus den verbindlichen
 * Architekt-Klarstellungen (Spec-Review Iter 1, 2026-06-10).
 */

import type { AdminSubscription, RenewalEvent } from '@/types/admin-subscription';
import { formatCents, getIntlLocale } from './format';

/**
 * Betragsformat (Spec 0222 §6):
 * - amount fehlt → "—"
 * - Currency "EUROCENT" → formatCents (Wert ist in Cent)
 * - ISO-4217-Code (3 Grossbuchstaben) → als Hauptwaehrungseinheit formatieren
 * - unbekannter Code → Rohwert + Code
 * - Currency fehlt → Rohwert
 */
export function formatChargeAmount(
  amount?: number | null,
  currency?: string | null
): string {
  if (amount == null || typeof amount !== 'number' || isNaN(amount)) return '—';

  if (currency === 'EUROCENT') {
    return formatCents(amount);
  }

  if (currency && /^[A-Z]{3}$/.test(currency)) {
    // Intl akzeptiert jeden wohlgeformten 3-Buchstaben-Code; unbekannte Codes
    // rendert es selbst als "Wert + Code" (z. B. "499,00 XXY") — deckt §6 ab.
    return new Intl.NumberFormat(getIntlLocale(), {
      style: 'currency',
      currency,
    }).format(amount);
  }

  return currency ? `${amount} ${currency}` : String(amount);
}

/**
 * K1 — eventType-Fallback fuer pre-0203-Payloads:
 * fehlt eventType, aus status ableiten (charged → create, renewed → renewal).
 * Fehlen beide → undefined (neutrale Timeline-Zeile ohne Badge).
 */
export function deriveEventType(event: RenewalEvent): 'create' | 'renewal' | undefined {
  if (event.eventType === 'create' || event.eventType === 'renewal') {
    return event.eventType;
  }
  if (event.status === 'charged') return 'create';
  if (event.status === 'renewed') return 'renewal';
  return undefined;
}

/**
 * K2 — Erst-Transaktion NICHT als erstes Array-Element nach Sort bestimmen:
 * Vorrang hat das Event mit eventType=create (nach K1-Fallback) und gesetzter
 * originalTransactionId; da originalTransactionId auf JEDEM Event auf die
 * Create-TX zeigt, ist sonst jedes Event mit dem Feld valide Quelle.
 * Fehlt das Feld ueberall (pre-0203-Stages) → undefined (Zeile ausblenden).
 */
export function findInitialTransactionId(renewals: RenewalEvent[]): string | undefined {
  const createEvent = renewals.find(
    (e) => deriveEventType(e) === 'create' && e.originalTransactionId
  );
  if (createEvent?.originalTransactionId) return createEvent.originalTransactionId;

  const anyWithField = renewals.find((e) => e.originalTransactionId);
  return anyWithField?.originalTransactionId ?? undefined;
}

/**
 * Partial-Erkennung (Spec 0222 §5, F4): nur wenn das Listen-renewalCount
 * vorhanden ist UND mehr Charges meldet, als der Endpoint Events liefert.
 * Fehlt das Listenfeld → kein Hinweis (false).
 */
export function isPartialHistory(
  listRenewalCount: number | null | undefined,
  events: RenewalEvent[]
): boolean {
  return listRenewalCount != null && listRenewalCount > events.length;
}

/**
 * Gesamtumsatz (Spec 0222 §6, F3) — undefined (Zeile ausblenden) wenn:
 * Partial, Truncated, keine Events, irgendein Event ohne amount,
 * oder Waehrungs-Mix (inkl. fehlender currency).
 */
export function computeTotalRevenue(
  events: RenewalEvent[],
  opts: { partial: boolean; truncated: boolean }
): { amount: number; currency: string } | undefined {
  if (opts.partial || opts.truncated) return undefined;
  if (events.length === 0) return undefined;

  const currency = events[0].currency;
  if (!currency) return undefined;

  let sum = 0;
  for (const event of events) {
    if (event.amount == null) return undefined;
    if (event.currency !== currency) return undefined;
    sum += event.amount;
  }

  return { amount: sum, currency };
}

/**
 * Defensive Sortierung nach processedAt absteigend (Spec 0222 §10):
 * Backfill koennte die Event-Reihenfolge aendern — wir verlassen uns nicht
 * auf die Facade-Sortierung. Nicht parsebare Timestamps sortieren ans Ende.
 */
export function sortRenewalsDesc(events: RenewalEvent[]): RenewalEvent[] {
  return [...events].sort((a, b) => {
    const ta = Date.parse(a.processedAt);
    const tb = Date.parse(b.processedAt);
    return (isNaN(tb) ? -Infinity : tb) - (isNaN(ta) ? -Infinity : ta);
  });
}

// ============================================================
// SKELETON-0283 — rekonstruierte Timeline fuer Alt-Subscriptions (Spec 283)
// Rueckbau: kann komplett entfernt werden, sobald der Facade-Backfill
// echte Events liefert (Vorrang echter Events baut die Synthese ohnehin
// automatisch zurueck).
// ============================================================

/** Service-Preis aus dem Join (0222 B7) — Basis der Betragsschaetzungen. */
export interface ServicePriceInfo {
  /** kleinste Waehrungseinheit, analog RenewalEvent.amount */
  amount: number;
  /** Waehrung stets aus dem Join, nie hart EUR (Spec 283 §4) */
  currency: string;
}

/** Aus Subscription-Metadaten abgeleitetes Skeleton-Event (Spec 283 §4). */
export interface DerivedRenewalEvent {
  source: 'derived';
  kind: 'initial' | 'aggregate' | 'last' | 'termination';
  /** Anzeige-/Sortierdatum, ISO 8601 — echt aus den Subscription-Metadaten */
  occurredAt: string;
  /** aggregate: N zusammengefasste Belastungen */
  count?: number;
  /** aggregate: Zeitraum-Start (= createdAt) */
  periodStart?: string;
  /** aggregate: Zeitraum-Ende (= lastRenewalAt); fehlt bei Degradierung */
  periodEnd?: string;
  /** Schaetzbetrag in kleinster Einheit; undefined = "Betrag unbekannt" */
  estimatedAmount?: number;
  currency?: string;
  /** initial: Erst-TX-Link via 0222-Ableitung, falls auffindbar */
  initialTransactionId?: string;
}

/**
 * Discriminated Union (Spec 283 §6): abgeleitete Events duerfen nie
 * versehentlich Code-Pfade fuer echte Facade-Events durchlaufen.
 */
export interface FacadeTimelineEntry {
  source: 'facade';
  event: RenewalEvent;
}

export type RenewalTimelineEntry = FacadeTimelineEntry | DerivedRenewalEvent;

/**
 * SKELETON-0283 — Zaehlannahme renewalCount (Spec 283 §4/R1):
 * Arbeitsannahme "exklusiv" = renewalCount zaehlt Belastungen OHNE die
 * Erst-Abrechnung; E1 wird darum NICHT von N subtrahiert. Per Dev-E2E gegen
 * SIQ202606021231429295 nicht falsifizierbar (Off-by-one bei N=1226 nicht
 * messbar); steht die inkl.-Semantik fest (Facade-Gap G2), hier auf true
 * setzen — die E2-Formel zieht dann zusaetzlich 1 ab.
 */
export const RENEWAL_COUNT_INCLUDES_INITIAL = false;

/**
 * Defensive Filterung (Facade-Gap G1, Handover 2026-06-12): Events ohne
 * parsebaren Zeitstempel fliegen vor Sortierung/Synthese raus und zaehlen
 * fuer die Skeleton-Logik als nicht vorhanden.
 */
export function filterEventsWithTimestamp(events: RenewalEvent[]): RenewalEvent[] {
  return events.filter(
    (e) => typeof e.processedAt === 'string' && !isNaN(Date.parse(e.processedAt))
  );
}

function isSameUtcDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getUTCFullYear() === db.getUTCFullYear() &&
    da.getUTCMonth() === db.getUTCMonth() &&
    da.getUTCDate() === db.getUTCDate()
  );
}

/**
 * Skeleton-Ableitung (Spec 283 §4): nur bei erfolgreich geladener, aber
 * leerer/partieller Historie mit gemeldeten Belastungen. Der Fehler-Fall
 * (Fetch fehlgeschlagen) liegt VOR diesem Aufruf — Synthese darf niemals
 * einen Ausfall maskieren, die Komponente ruft die Funktion dann nicht auf.
 */
export function deriveSkeletonEvents(
  subscription: Pick<
    AdminSubscription,
    'createdAt' | 'renewalCount' | 'lastRenewalAt' | 'terminatedAt' | 'status'
  >,
  rawEvents: RenewalEvent[],
  servicePrice?: ServicePriceInfo
): DerivedRenewalEvent[] {
  // Datenmuell-Guard: renewalCount negativ/NaN -> 0 -> Empty-State unveraendert
  const rc = subscription.renewalCount;
  const listRenewalCount =
    typeof rc === 'number' && Number.isFinite(rc) && rc > 0 ? Math.floor(rc) : 0;
  if (listRenewalCount === 0) return [];

  // Fehlt createdAt -> keine Synthese (nur bestehender Partial-Hinweis)
  const createdMs = Date.parse(subscription.createdAt ?? '');
  if (isNaN(createdMs)) return [];

  // G1-Filter: Events ohne Timestamp zaehlen als nicht vorhanden
  const events = filterEventsWithTimestamp(rawEvents);

  // Vollstaendige Historie -> keinerlei Synthese
  if (!isPartialHistory(listRenewalCount, events)) return [];

  // Datenmuell-Guard: lastRenewalAt < createdAt -> wie fehlend behandeln
  const lastMs = Date.parse(subscription.lastRenewalAt ?? '');
  const lastRenewalAt =
    !isNaN(lastMs) && lastMs >= createdMs ? (subscription.lastRenewalAt as string) : undefined;

  const amount =
    servicePrice && typeof servicePrice.amount === 'number' && Number.isFinite(servicePrice.amount)
      ? servicePrice.amount
      : undefined;
  const currency = amount != null ? servicePrice?.currency : undefined;

  const derived: DerivedRenewalEvent[] = [];

  // E4 Terminierung — Datum echt, kein Betrag
  if (
    subscription.status === 'terminated' &&
    subscription.terminatedAt &&
    !isNaN(Date.parse(subscription.terminatedAt))
  ) {
    derived.push({
      source: 'derived',
      kind: 'termination',
      occurredAt: subscription.terminatedAt,
    });
  }

  // E3 Letzte Belastung — unterdrueckt, wenn ein echtes Event >= lastRenewalAt
  // existiert ODER am selben UTC-Kalendertag liegt (Facade-Timestamps weichen
  // sekundenweise ab, striktes >= reicht nicht — Spec 283 §4).
  let e3Rendered = false;
  if (lastRenewalAt) {
    const suppressed = events.some((e) => {
      const t = Date.parse(e.processedAt);
      return t >= lastMs || isSameUtcDay(t, lastMs);
    });
    if (!suppressed) {
      e3Rendered = true;
      derived.push({
        source: 'derived',
        kind: 'last',
        occurredAt: lastRenewalAt,
        ...(amount != null ? { estimatedAmount: amount, currency } : {}),
      });
    }
  }

  // E2 Aggregat — N = renewalCount − k − (1 wenn E3 synthetisch), k = echte
  // Nicht-Initial-Events nach G1-Filterung; E1 steckt nicht in renewalCount
  // (exkl.-Annahme, siehe RENEWAL_COUNT_INCLUDES_INITIAL).
  const k = events.filter((e) => deriveEventType(e) !== 'create').length;
  const n =
    listRenewalCount -
    k -
    (e3Rendered ? 1 : 0) -
    (RENEWAL_COUNT_INCLUDES_INITIAL ? 1 : 0);
  if (n > 0) {
    derived.push({
      source: 'derived',
      kind: 'aggregate',
      occurredAt: lastRenewalAt ?? subscription.createdAt,
      count: n,
      periodStart: subscription.createdAt,
      ...(lastRenewalAt ? { periodEnd: lastRenewalAt } : {}),
      ...(amount != null ? { estimatedAmount: n * amount, currency } : {}),
    });
  }

  // E1 Erst-Abrechnung — nur wenn kein echtes Initial-Event existiert
  // (Erkennung ueber die bestehende 0222-Klassifikation, keine neue Heuristik)
  const hasRealInitial = events.some((e) => deriveEventType(e) === 'create');
  if (!hasRealInitial) {
    const initialTransactionId = findInitialTransactionId(events);
    derived.push({
      source: 'derived',
      kind: 'initial',
      occurredAt: subscription.createdAt,
      ...(amount != null ? { estimatedAmount: amount, currency } : {}),
      ...(initialTransactionId ? { initialTransactionId } : {}),
    });
  }

  return derived;
}

// Tie-Break bei identischem Zeitstempel: Terminierung zuoberst, dann letzte
// Belastung, echte Events vor dem Aggregat, Erst-Abrechnung zuunterst.
const ENTRY_RANK: Record<string, number> = {
  termination: 40,
  last: 30,
  facade: 25,
  aggregate: 20,
  initial: 10,
};

function entryTime(entry: RenewalTimelineEntry): number {
  return entry.source === 'facade'
    ? Date.parse(entry.event.processedAt)
    : Date.parse(entry.occurredAt);
}

function entryRank(entry: RenewalTimelineEntry): number {
  return entry.source === 'facade' ? ENTRY_RANK.facade : ENTRY_RANK[entry.kind];
}

/**
 * Merged Timeline (Spec 283 §4): echte Events bleiben unveraendert stehen,
 * Skeleton-Events fuellen nur Luecken; gemeinsam absteigend nach Zeit.
 */
export function buildRenewalTimeline(
  rawEvents: RenewalEvent[],
  skeleton: DerivedRenewalEvent[]
): RenewalTimelineEntry[] {
  const entries: RenewalTimelineEntry[] = [
    ...filterEventsWithTimestamp(rawEvents).map(
      (event): FacadeTimelineEntry => ({ source: 'facade', event })
    ),
    ...skeleton,
  ];
  return entries.sort((a, b) => {
    const diff = entryTime(b) - entryTime(a);
    return diff !== 0 ? diff : entryRank(b) - entryRank(a);
  });
}

/** Zahlformat fuer das Aggregat-N (lokalisiert, z.B. "1.225" in de). */
export function formatEventCount(count: number): string {
  return new Intl.NumberFormat(getIntlLocale()).format(count);
}
